<template>
  <SidebarComponent />

  <div class="dashboard-layout orders-accounting-page">
    <div class="content-container">
      <div class="card-container">
        <div class="titolo-card">
          <h1>Contabilità ordini</h1>
          <p class="subtitle">Riepilogo degli incassi su ordini manuali, legacy e WooCommerce</p>
        </div>

        <section v-if="!authReady || !canViewTotals" class="card-surface" role="status">
          <p class="muted">
            {{ authReady ? 'La visualizzazione dei totali è riservata agli account autorizzati.' : 'Verifica accesso…' }}
          </p>
        </section>

        <template v-if="canViewTotals">
        <section class="card-surface filters-card">
          <div class="filters-row">
            <div class="filters-group">
              <label for="start-date">Dal</label>
              <input id="start-date" type="date" v-model="filters.startDate" />
            </div>
            <div class="filters-group">
              <label for="end-date">Al</label>
              <input id="end-date" type="date" v-model="filters.endDate" />
            </div>
            <div class="filters-group quick-group">
              <label>Intervalli rapidi</label>
              <div class="quick-range">
                <button
                  v-for="range in quickRanges"
                  :key="range.value"
                  type="button"
                  class="btn tiny"
                  :class="activeRange === range.value ? 'primary' : 'ghost'"
                  @click="applyQuickRange(range.value)"
                >
                  {{ range.label }}
                </button>
              </div>
            </div>
            <div class="filters-group">
              <label for="studio-filter">Studio</label>
              <select id="studio-filter" v-model="filters.studio">
                <option value="">Tutti</option>
                <option v-for="studio in studioOptions" :key="studio" :value="studio">
                  {{ studio }}
                </option>
              </select>
            </div>
            <div class="filters-actions">

              <button
                type="button"
                class="btn ghost staff-button-neutral"
                @click="exportCsv"
                :disabled="loading || !exportableOrders.length"
              >
                Esporta CSV
              </button>
              <button
                type="button"
                class="btn ghost staff-button-neutral"
                @click="exportDailySummaryCsv"
                :disabled="loading || !exportableOrders.length"
              >
                Esporta riepilogo
              </button>
              <button type="button" class="btn primary staff-button-neutral" @click="loadOrders" :disabled="loading">
                {{ loading ? 'Aggiornamento…' : 'Aggiorna' }}
              </button>
                <button
                type="button"
                class="btn ai staff-button-neutral"
                @click="goToAiAnalysis"
              >
                Analisi tramite AI
              </button>
            </div>
          </div>
          <p v-if="lastUpdatedAt" class="filters-note">
            Ultimo aggiornamento: {{ formatDateTime(lastUpdatedAt) }}
            <span v-if="orders.length"> · Ordini rate manuali caricati: {{ formatNumber(rateManualOrdersLoadedCount) }}</span>
          </p>
        </section>

        <section class="card-surface" v-if="error">
          <p class="error-state">{{ error }}</p>
        </section>

        <section class="card-surface" v-if="loading">
          <p class="muted">Caricamento ordini…</p>
        </section>

        <template v-if="!loading && !error">
          <section class="card-surface metrics-card">
            <p v-if="limitedByMaxDocs" class="muted note">Sono stati considerati solo gli ultimi {{ maxDocs }} ordini per velocizzare il calcolo. Applica un intervallo data per analizzare periodi specifici.</p>
            <div class="metrics-grid">
              <div class="metric-box">
                <span class="metric-label">Incassato</span>
                <span class="metric-value">{{ formatCurrency(totalRevenue) }}</span>
              </div>
              <div class="metric-box">
                <span class="metric-label">Ordini pagati</span>
                <span class="metric-value">{{ paidOrderCount }}</span>
                <span class="metric-sub">Ticket medio {{ formatCurrency(averageTicket) }}</span>
              </div>
              <div class="metric-box">
                <span class="metric-label">Media giornaliera</span>
                <span class="metric-value">{{ formatCurrency(dailyAverage) }}</span>
                <span class="metric-sub">Su {{ dailyTotals.length }} giorni con incassi</span>
              </div>
              <div class="metric-box">
                <span class="metric-label">Incassi coaching</span>
                <span class="metric-value">{{ formatCurrency(coachingRevenue) }}</span>
                <span class="metric-sub" v-if="totalRevenue">{{ formatPercent(coachingRevenue / totalRevenue) }} del totale</span>
                <span class="metric-sub" v-else>—</span>
              </div>
            </div>
            <p v-if="!paidOrderCount" class="muted">Nessun ordine con pagamento completato nel periodo selezionato.</p>
          </section>

          <section class="card-surface accounting-table-section" v-if="paidOrderCount">
            <h2 class="card-title">Metodi di pagamento</h2>
            <table class="summary-table accounting-summary-table" aria-label="Metodi di pagamento" role="table">
              <thead role="rowgroup">
                <tr role="row">
                  <th scope="col" role="columnheader">Metodo</th>
                  <th scope="col" role="columnheader">Ordini</th>
                  <th scope="col" role="columnheader">Incassato</th>
                  <th scope="col" role="columnheader">%</th>
                </tr>
              </thead>
              <tbody role="rowgroup">
                <tr role="row" v-for="row in paymentBreakdown" :key="row.key">
                  <td data-label="Metodo" role="cell" class="accounting-cell-title">{{ row.label }}</td>
                  <td data-label="Ordini" role="cell">{{ row.count }}</td>
                  <td data-label="Incassato" role="cell" class="accounting-cell-featured">{{ formatCurrency(row.amount) }}</td>
                  <td data-label="Quota sul totale" role="cell">{{ formatPercent(row.percent) }}</td>
                </tr>
              </tbody>
            </table>
          </section>

          <section class="card-surface accounting-table-section" v-if="categoryBreakdown.length">
            <h2 class="card-title">Categorie prodotti</h2>
            <table class="summary-table accounting-summary-table" aria-label="Categorie prodotti" role="table">
              <thead role="rowgroup">
                <tr role="row">
                  <th scope="col" role="columnheader">Categoria</th>
                  <th scope="col" role="columnheader">Ordini</th>
                  <th scope="col" role="columnheader">Quantità</th>
                  <th scope="col" role="columnheader">Incassato</th>
                  <th scope="col" role="columnheader">%</th>
                </tr>
              </thead>
              <tbody role="rowgroup">
                <tr role="row" v-for="row in categoryBreakdown" :key="row.key">
                  <td data-label="Categoria" role="cell" class="accounting-cell-title">{{ row.label }}</td>
                  <td data-label="Ordini" role="cell">{{ formatNumber(row.orders) }}</td>
                  <td data-label="Quantità" role="cell">{{ formatNumber(row.quantity) }}</td>
                  <td data-label="Incassato" role="cell" class="accounting-cell-featured">{{ formatCurrency(row.amount) }}</td>
                  <td data-label="Quota sul totale" role="cell">{{ formatPercent(row.percent) }}</td>
                </tr>
              </tbody>
            </table>
          </section>

          <section class="card-surface accounting-table-section" v-if="manualInstallmentsOverview.totalCount">
            <h2 class="card-title">Rate manuali</h2>
            <p class="muted note">
              Conteggia le rate incassate con <code>paidAt</code> (se assente usa <code>scheduledDate</code>) e le scadute con <code>scheduledDate</code> nel periodo selezionato.
            </p>
            <table class="summary-table accounting-summary-table" aria-label="Rate manuali" role="table">
              <thead role="rowgroup">
                <tr role="row">
                  <th scope="col" role="columnheader">Stato</th>
                  <th scope="col" role="columnheader">Rate</th>
                  <th scope="col" role="columnheader">Importo</th>
                  <th scope="col" role="columnheader">%</th>
                </tr>
              </thead>
              <tbody role="rowgroup">
                <tr role="row">
                  <td data-label="Stato" role="cell" class="accounting-cell-title">Incassate</td>
                  <td data-label="Rate" role="cell">{{ formatNumber(manualInstallmentsOverview.paidCount) }}</td>
                  <td data-label="Importo" role="cell" class="accounting-cell-featured">{{ formatCurrency(manualInstallmentsOverview.paidAmount) }}</td>
                  <td data-label="Quota sul totale" role="cell">{{ formatPercent(manualInstallmentsOverview.totalAmount ? manualInstallmentsOverview.paidAmount / manualInstallmentsOverview.totalAmount : 0) }}</td>
                </tr>
                <tr role="row">
                  <td data-label="Stato" role="cell" class="accounting-cell-title">Scadute</td>
                  <td data-label="Rate" role="cell">{{ formatNumber(manualInstallmentsOverview.expiredCount) }}</td>
                  <td data-label="Importo" role="cell" class="accounting-cell-featured">{{ formatCurrency(manualInstallmentsOverview.expiredAmount) }}</td>
                  <td data-label="Quota sul totale" role="cell">{{ formatPercent(manualInstallmentsOverview.totalAmount ? manualInstallmentsOverview.expiredAmount / manualInstallmentsOverview.totalAmount : 0) }}</td>
                </tr>
                <tr role="row">
                  <td data-label="Stato" role="cell" class="accounting-cell-title">Totale (incassate + scadute)</td>
                  <td data-label="Rate" role="cell">{{ formatNumber(manualInstallmentsOverview.totalCount) }}</td>
                  <td data-label="Importo" role="cell" class="accounting-cell-featured">{{ formatCurrency(manualInstallmentsOverview.totalAmount) }}</td>
                  <td data-label="Quota sul totale" role="cell">{{ formatPercent(1) }}</td>
                </tr>
              </tbody>
            </table>
          </section>

          <section class="card-surface accounting-table-section" v-if="ptProductsDebug.length">
            <h2 class="card-title">Prodotti conteggiati come PT</h2>
            <p class="muted note">Debug temporaneo: elenco dei prodotti riconosciuti nella categoria PT con quantità e incasso totale.</p>
            <table class="summary-table accounting-summary-table" aria-label="Prodotti conteggiati come PT" role="table">
              <thead role="rowgroup">
                <tr role="row">
                  <th scope="col" role="columnheader">Prodotto</th>
                  <th scope="col" role="columnheader">Ordini</th>
                  <th scope="col" role="columnheader">Quantità</th>
                  <th scope="col" role="columnheader">Incassato</th>
                </tr>
              </thead>
              <tbody role="rowgroup">
                <tr role="row" v-for="row in ptProductsDebug" :key="row.name">
                  <td data-label="Prodotto" role="cell" class="accounting-cell-title">{{ row.name }}</td>
                  <td data-label="Ordini" role="cell">{{ formatNumber(row.orders) }}</td>
                  <td data-label="Quantità" role="cell">{{ formatNumber(row.quantity) }}</td>
                  <td data-label="Incassato" role="cell" class="accounting-cell-featured">{{ formatCurrency(row.amount) }}</td>
                </tr>
              </tbody>
            </table>
          </section>

          <section class="card-surface accounting-table-section" v-if="ptProductsMissing.length">
            <h2 class="card-title">Prodotti PT non classificati</h2>
            <p class="muted note">Righe d'ordine con nome contenente "PT" ma classificate in altre categorie.</p>
            <table class="summary-table accounting-summary-table" aria-label="Prodotti PT non classificati" role="table">
              <thead role="rowgroup">
                <tr role="row">
                  <th scope="col" role="columnheader">Ordine</th>
                  <th scope="col" role="columnheader">Prodotto</th>
                  <th scope="col" role="columnheader">Categoria rilevata</th>
                  <th scope="col" role="columnheader">Quantità</th>
                  <th scope="col" role="columnheader">Incassato</th>
                </tr>
              </thead>
              <tbody role="rowgroup">
                <tr role="row" v-for="row in ptProductsMissing" :key="row.orderId + '-' + row.name">
                  <td data-label="Ordine" role="cell" class="accounting-cell-wide">{{ row.orderId || '—' }}</td>
                  <td data-label="Prodotto" role="cell" class="accounting-cell-title">{{ row.name }}</td>
                  <td data-label="Categoria rilevata" role="cell" class="accounting-cell-wide">{{ categoryLabel(row.resolvedCategory) }}</td>
                  <td data-label="Quantità" role="cell">{{ formatNumber(row.quantity) }}</td>
                  <td data-label="Incassato" role="cell" class="accounting-cell-featured">{{ formatCurrency(row.amount) }}</td>
                </tr>
              </tbody>
            </table>
          </section>
          <section class="card-surface accounting-table-section" v-if="dailyTotals.length">
            <h2 class="card-title">Trend giornaliero</h2>
            <table class="summary-table accounting-summary-table" aria-label="Trend giornaliero" role="table">
              <thead role="rowgroup">
                <tr role="row">
                  <th scope="col" role="columnheader">Data</th>
                  <th scope="col" role="columnheader">Ordini Coaching</th>
                  <th scope="col" role="columnheader">Incassato Coaching</th>
                  <th scope="col" role="columnheader">Ordini Sedute PT</th>
                  <th scope="col" role="columnheader">Incassato Sedute PT</th>
                  <th scope="col" role="columnheader">Ordini totali</th>
                  <th scope="col" role="columnheader">Incassato totale</th>
                </tr>
              </thead>
              <tbody role="rowgroup">
                <tr role="row" v-for="row in dailyTotals" :key="row.date">
                  <td data-label="Data" role="cell" class="accounting-cell-title">{{ formatDisplayDate(row.date) }}</td>
                  <td data-label="Ordini Coaching" role="cell">{{ formatNumber(row.coachingCount) }}</td>
                  <td data-label="Incassato Coaching" role="cell">{{ formatCurrency(row.coachingAmount) }}</td>
                  <td data-label="Ordini Sedute PT" role="cell">{{ formatNumber(row.ptCount) }}</td>
                  <td data-label="Incassato Sedute PT" role="cell">{{ formatCurrency(row.ptAmount) }}</td>
                  <td data-label="Ordini totali" role="cell" class="accounting-cell-wide accounting-cell-total-count">{{ formatNumber(row.count) }}</td>
                  <td data-label="Incassato totale" role="cell" class="accounting-cell-featured">{{ formatCurrency(row.amount) }}</td>
                </tr>
              </tbody>
            </table>
          </section>
        </template>
        </template>
      </div>
    </div>
  </div>
</template>

<script>
import SidebarComponent from '../SidebarComponent.vue';
import { getAuth, onAuthStateChanged } from 'firebase/auth';
import { getFirestore, collection, getDocs, query, where, orderBy, limit, doc, getDoc } from 'firebase/firestore';
import { canViewOrderAccountingTotals } from '@/lib/orderAccountingAccess';
import {
  isManualInstallmentOperationallyExpired,
} from '@/lib/manualInstallments';

export default {
  name: 'OrdersAccounting',
  components: { SidebarComponent },
  data() {
    return {
      db: null,
      authReady: false,
      accountingUserUid: '',
      accountingAccessVersion: 0,
      authUnsubscribe: null,
      orders: [],
      loading: false,
      error: '',
      filters: {
        startDate: '',
        endDate: '',
        studio: '',
      },
      quickRanges: [
        { label: 'Ultimi 7 gg', value: '7d' },
        { label: 'Ultimi 30 gg', value: '30d' },
        { label: 'Mese corrente', value: 'month' },
        { label: 'Anno corrente', value: 'year' },
      ],
      activeRange: null,
      lastUpdatedAt: null,
      maxDocs: 1200,
      limitedByMaxDocs: false,
      catalogProducts: [],
      productIndex: {
        byId: Object.create(null),
        bySku: Object.create(null),
        byName: Object.create(null),
      },
      productsLoaded: false,
      studioOptions: [
        'Tivoli Quartier generale',
        'FF Genova',
        'FF Bologna',
        'FF Bari',
        'FF Pescara',
        'FF Firenze',
        'FF Torino',
        'FF Napoli',
        'FF Milano',
        'FF Milano Sempione',
        'FF Milano Citta Studi',
        'FF Roma',
        'FF Roma Eur',
        'FF Salerno',
        'FF Cagliari',
        'FF Padova',
      ],
      userStudioCache: {},
      userProfileCache: {},
      userProfileRequests: new Set(),
    };
  },
  created() {
    this.db = getFirestore();
    const initial = this.computeRange('month');
    this.filters.startDate = initial.start;
    this.filters.endDate = initial.end;
    this.activeRange = 'month';
    this.authUnsubscribe = onAuthStateChanged(getAuth(), (user) => {
      this.accountingAccessVersion += 1;
      this.accountingUserUid = user?.uid || '';
      this.authReady = true;
      this.orders = [];
      this.catalogProducts = [];
      this.buildProductIndex([]);
      this.productsLoaded = false;
      this.userStudioCache = {};
      this.userProfileCache = {};
      this.userProfileRequests = new Set();
      this.lastUpdatedAt = null;
      this.limitedByMaxDocs = false;
      this.loading = false;
      this.error = '';
      if (this.canViewTotals) this.initializeData();
    });
  },
  beforeUnmount() {
    this.authUnsubscribe?.();
    this.accountingAccessVersion += 1;
    this.accountingUserUid = '';
  },
  watch: {
    'filters.startDate'() {
      this.syncDateRange('start');
      this.activeRange = this.detectActiveRange();
    },
    'filters.endDate'() {
      this.syncDateRange('end');
      this.activeRange = this.detectActiveRange();
    },
    'filters.studio'(newValue) {
      if (newValue) {
        this.ensureStudiosForOrders();
      }
    },
  },
  computed: {
    canViewTotals() {
      return this.authReady && canViewOrderAccountingTotals({ uid: this.accountingUserUid });
    },
    rateManualOrdersLoadedCount() {
      return this.orders.filter(order => this.isManualInstallmentsOrder(order)).length;
    },
    exportableOrders() {
      return this.paidOrders;
    },
    paidOrders() {
      const list = [];
      for (const order of this.orders) {
        if (this.isManualInstallmentsOrder(order)) {
          // Conta sempre le singole rate pagate (anche se l'ordine è "in attesa di pagamento").
          const expanded = this.expandManualInstallmentOrders(order);
          for (const part of expanded) {
            if (this.isWithinDate(part) && this.orderMatchesStudio(part)) {
              list.push(part);
            }
          }
        } else if (this.isOrderPaid(order) && this.isWithinDate(order) && this.orderMatchesStudio(order)) {
          list.push(order);
        }
      }
      return list;
    },
    paidOrderCount() {
      const ids = new Set();
      for (const order of this.paidOrders) {
        ids.add(order._id || order.orderId || order.id || JSON.stringify(order));
      }
      return ids.size;
    },
    totalRevenue() {
      return this.paidOrders.reduce((sum, order) => sum + this.resolveAmount(order), 0);
    },
    averageTicket() {
      return this.paidOrderCount ? this.totalRevenue / this.paidOrderCount : 0;
    },
    dailyAverage() {
      if (!this.dailyTotals.length) return 0;
      return this.totalRevenue / this.dailyTotals.length;
    },
    coachingRevenue() {
      if (!this.dailyTotals.length) return 0;
      return this.dailyTotals.reduce((sum, row) => sum + (row.coachingAmount || 0), 0);
    },
    paymentBreakdown() {
      const totals = new Map();
      for (const order of this.paidOrders) {
        const key = this.normalizePaymentKey(order);
        const label = this.paymentLabel(key);
        const amount = this.resolveAmount(order);
        if (!totals.has(key)) {
          totals.set(key, { key, label, count: 0, amount: 0 });
        }
        const entry = totals.get(key);
        entry.count += 1;
        entry.amount += amount;
      }
      const total = this.totalRevenue || 0;
      return Array.from(totals.values())
        .map(entry => ({
          ...entry,
          percent: total > 0 ? entry.amount / total : 0,
        }))
        .sort((a, b) => b.amount - a.amount);
    },

    categoryBreakdown() {
      const totals = new Map();
      const totalRevenue = this.totalRevenue || 0;
      for (const order of this.paidOrders) {
        const categoriesSeen = new Set();
        const products = this.safeProducts(order);
        let distributed = 0;
        let sawCategory = false;

        if (products.length) {
          for (const line of products) {
            const catalogProduct = this.matchCatalogProduct(line) || {};
            const displayName = this.resolveLineDisplayName(line, catalogProduct);
            const safeLine = { ...line, name: displayName };
            const categoryKey = this.resolveProductCategory(safeLine, order);
            const amount = this.resolveLineAccountingAmount(safeLine, order);
            const quantity = this.resolveLineQuantity(safeLine);
            if (!totals.has(categoryKey)) {
              totals.set(categoryKey, {
                key: categoryKey,
                label: this.categoryLabel(categoryKey),
                orders: 0,
                lines: 0,
                quantity: 0,
                amount: 0,
              });
            }
            const entry = totals.get(categoryKey);
            entry.lines += 1;
            entry.quantity += quantity;
            entry.amount += amount;
            categoriesSeen.add(categoryKey);
            if (Number.isFinite(amount) && amount !== 0) {
              distributed += amount;
              sawCategory = true;
            }
          }
        } else {
          // Ordini senza righe prodotto: li consideriamo coaching (stessa logica del trend giornaliero).
          const categoryKey = 'coaching';
          const amount = this.resolveAmount(order);
          if (amount) {
            if (!totals.has(categoryKey)) {
              totals.set(categoryKey, {
                key: categoryKey,
                label: this.categoryLabel(categoryKey),
                orders: 0,
                lines: 0,
                quantity: 0,
                amount: 0,
              });
            }
            const entry = totals.get(categoryKey);
            entry.lines += 1;
            entry.amount += amount;
            categoriesSeen.add(categoryKey);
            distributed += amount;
          }
        }

        // Se le righe prodotto non hanno importi utili, consideriamo l'ordine coaching (stessa logica del trend).
        if (products.length && (!sawCategory || distributed === 0)) {
          const categoryKey = 'coaching';
          const amount = this.resolveAmount(order);
          if (amount) {
            if (!totals.has(categoryKey)) {
              totals.set(categoryKey, {
                key: categoryKey,
                label: this.categoryLabel(categoryKey),
                orders: 0,
                lines: 0,
                quantity: 0,
                amount: 0,
              });
            }
            const entry = totals.get(categoryKey);
            entry.lines += 1;
            entry.amount += amount;
            categoriesSeen.add(categoryKey);
            distributed = amount;
            sawCategory = true;
          }
        }

        // Rende visibili eventuali differenze (sconti/spese/arrotondamenti) tra totale ordine e somma righe.
        const orderAmount = this.resolveAmount(order);
        if (products.length && sawCategory && distributed !== 0) {
          const diff = orderAmount - distributed;
          if (Math.abs(diff) > 0.01) {
            const categoryKey = 'adjustments';
            if (!totals.has(categoryKey)) {
              totals.set(categoryKey, {
                key: categoryKey,
                label: this.categoryLabel(categoryKey),
                orders: 0,
                lines: 0,
                quantity: 0,
                amount: 0,
              });
            }
            const entry = totals.get(categoryKey);
            entry.lines += 1;
            entry.amount += diff;
            categoriesSeen.add(categoryKey);
          }
        }

        for (const categoryKey of categoriesSeen) {
          const entry = totals.get(categoryKey);
          if (entry) entry.orders += 1;
        }
      }
      return Array.from(totals.values())
        .map((entry) => ({
          ...entry,
          percent: totalRevenue > 0 ? entry.amount / totalRevenue : 0,
        }))
        .sort((a, b) => b.amount - a.amount);
    },
    ptProductsDebug() {
      const totals = new Map();
      for (const order of this.paidOrders) {
        const orderId = order._id || order.orderId || order.id || '';
        for (const line of this.safeProducts(order)) {
          const catalogProduct = this.matchCatalogProduct(line) || {};
          const displayName = this.resolveLineDisplayName(line, catalogProduct);
          const safeLine = { ...line, name: displayName };
          const categoryKey = this.resolveProductCategory(safeLine, order);
          if (categoryKey !== 'pt') continue;
          const key = this.normalizeKey(displayName) || `${orderId}-${totals.size}`;
          if (!totals.has(key)) {
            totals.set(key, {
              name: displayName,
              orders: new Set(),
              quantity: 0,
              amount: 0,
            });
          }
          const entry = totals.get(key);
          entry.quantity += this.resolveLineQuantity(safeLine);
          entry.amount += this.resolveLineAccountingAmount(safeLine, order);
          entry.orders.add(orderId || key);
        }
      }
      return Array.from(totals.values())
        .map((entry) => ({
          ...entry,
          orders: entry.orders.size,
        }))
        .sort((a, b) => b.amount - a.amount);
    },
    ptProductsMissing() {
      const missing = [];
      for (const order of this.paidOrders) {
        const orderId = order._id || order.orderId || order.id || '';
        for (const line of this.safeProducts(order)) {
          const catalogProduct = this.matchCatalogProduct(line) || {};
          const displayName = this.resolveLineDisplayName(line, catalogProduct);
          const safeLine = { ...line, name: displayName };
          const nameKey = this.normalizeKey(displayName);
          if (!this.isPtProductName(nameKey)) continue;
          const categoryKey = this.resolveProductCategory(safeLine, order);
          if (categoryKey === 'pt') continue;
          missing.push({
            orderId,
            name: displayName,
            resolvedCategory: categoryKey || '(vuoto)',
            amount: this.resolveLineAccountingAmount(safeLine, order),
            quantity: this.resolveLineQuantity(safeLine),
          });
        }
      }
      return missing;
    },
    ptProductsAll() {
      const rows = [];
      for (const order of this.paidOrders) {
        const orderId = order._id || order.orderId || order.id || '';
        const orderDateObj = this.parseOrderDate(order);
        const orderDate = orderDateObj ? orderDateObj.toLocaleDateString('it-IT') : '—';
        const userEmail = order.userEmail || order.email || order.customer_email || '—';
        const status = order.status || order.orderStatus || '—';
        const paymentStatus = order.paymentStatus || order.payment_status || '—';
        const source = this.normalizeSource(order);
        for (const line of this.safeProducts(order)) {
          const catalogProduct = this.matchCatalogProduct(line) || {};
          const displayName = this.resolveLineDisplayName(line, catalogProduct);
          const safeLine = { ...line, name: displayName };
          const nameKey = this.normalizeKey(displayName);
          if (!this.isPtProductName(nameKey)) continue;
          const categoryKey = this.resolveProductCategory(safeLine, order);
          rows.push({
            orderId,
            orderDate,
            userEmail,
            status,
            paymentStatus,
            source,
            name: displayName,
            normalizedName: nameKey,
            resolvedCategory: categoryKey,
            quantity: this.resolveLineQuantity(safeLine),
            amount: this.resolveLineAccountingAmount(safeLine, order),
          });
        }
      }
      return rows;
    },
    manualInstallmentsOverview() {
      const overview = {
        paidCount: 0,
        paidAmount: 0,
        expiredCount: 0,
        expiredAmount: 0,
      };

      for (const order of this.orders) {
        if (!this.isManualInstallmentsOrder(order)) continue;
        if (!this.orderMatchesStudio(order)) continue;

        const plan = Array.isArray(order.manualInstallments) ? order.manualInstallments : [];
        for (const inst of plan) {
          const amount = Number(inst?.amount) || 0;
          if (!Number.isFinite(amount) || amount <= 0) continue;

          if (this.isInstallmentPaid(inst)) {
            const paidAt = this.resolveInstallmentPaidAt(inst, order);
            if (!paidAt) continue;
            if (!this.isWithinRangeDate(paidAt)) continue;
            overview.paidCount += 1;
            overview.paidAmount += amount;
            continue;
          }

          if (this.isInstallmentExpired(inst, order)) {
            const dueAt = this.installmentDueAt(inst);
            if (!dueAt) continue;
            if (!this.isWithinRangeDate(dueAt)) continue;
            overview.expiredCount += 1;
            overview.expiredAmount += amount;
          }
        }
      }

      overview.totalCount = overview.paidCount + overview.expiredCount;
      overview.totalAmount = overview.paidAmount + overview.expiredAmount;
      return overview;
    },
    dailyTotals() {
      const map = new Map();
      for (const order of this.paidOrders) {
        const when = this.parseAccountingDate(order);
        if (!when) continue;
        const key = this.formatDateKey(when);
        if (!map.has(key)) {
          map.set(key, {
            date: key,
            amount: 0,
            count: 0,
            coachingAmount: 0,
            ptAmount: 0,
            adjustmentAmount: 0,
            _coachingOrders: new Set(),
            _ptOrders: new Set(),
            _adjustmentOrders: new Set(),
            _sequence: 0,
          });
        }
        const entry = map.get(key);
        const orderAmount = this.resolveAmount(order);
        entry.amount += orderAmount;
        entry.count += 1;

        const rawOrderKey = this.normalizeKey(
          order._id ||
            order.orderId ||
            order.id ||
            order.uuid ||
            order.reference ||
            ''
        );
        const orderKey = rawOrderKey || `${key}-order-${entry._sequence}`;
        entry._sequence += 1;

        const products = this.safeProducts(order);
        let distributed = 0;
        let sawCategory = false;
        let orderPt = 0;
        let orderCoaching = 0;

        if (products.length) {
          for (const line of products) {
            const catalogProduct = this.matchCatalogProduct(line) || {};
            const displayName = this.resolveLineDisplayName(line, catalogProduct);
            const safeLine = { ...line, name: displayName };
            const normalizedCategory =
              this.normalizeCategory(
                this.resolveProductCategory(safeLine, order),
                safeLine,
                order
              ) || 'coaching';
            const lineAmount = this.resolveLineAmount(safeLine);
            if (!Number.isFinite(lineAmount) || lineAmount === 0) continue;
            distributed += lineAmount;
            sawCategory = true;
            if (normalizedCategory === 'pt') {
              orderPt += lineAmount;
              entry._ptOrders.add(orderKey);
            } else {
              orderCoaching += lineAmount;
              entry._coachingOrders.add(orderKey);
            }
          }
        }

        if (!sawCategory || distributed === 0) {
          orderCoaching = orderAmount;
          entry._coachingOrders.add(orderKey);
        } else {
          const diff = orderAmount - distributed;
          if (Math.abs(diff) > 0.01 && distributed !== 0) {
            // Scala proporzionalmente coaching/PT per allineare al totale incassato reale.
            const factor = orderAmount / distributed;
            orderPt *= factor;
            orderCoaching *= factor;
          }
        }

        entry.coachingAmount += orderCoaching;
        entry.ptAmount += orderPt;
      }

      return Array.from(map.values())
        .map((entry) => ({
          date: entry.date,
          amount: entry.amount,
          count: entry.count,
          coachingAmount: entry.coachingAmount,
          coachingCount: entry._coachingOrders.size,
          ptAmount: entry.ptAmount,
          ptCount: entry._ptOrders.size,
          adjustmentAmount: entry.adjustmentAmount || 0,
          adjustmentCount: entry._adjustmentOrders.size,
        }))
        .sort((a, b) => (a.date < b.date ? -1 : 1));
    },
  },
  methods: {
    hasAccountingAccess(version = this.accountingAccessVersion) {
      const user = getAuth().currentUser;
      return this.canViewTotals
        && version === this.accountingAccessVersion
        && user?.uid === this.accountingUserUid
        && canViewOrderAccountingTotals(user);
    },
    orderMatchesStudio(order) {
      const target = this.filters.studio;
      if (!target) return true;
      const studio = this.getOrderStudio(order);
      return studio === target;
    },
    getOrderStudio(order) {
      if (!order) return '';
      if (order._studio !== undefined) return order._studio;
      const direct = this.normalizeStudioName(
        order.studio ||
          order.userStudio ||
          order.customerStudio ||
          order.customer_studio ||
          order.customer?.studio ||
          order.billing?.studio ||
          order.user?.studio ||
          order.user?.studioName
      );
      const uid = this.getOrderUserUid(order);
      if (direct) {
        this.setOrderStudio(order, direct);
        if (uid) {
          if (this.$set) this.$set(this.userStudioCache, uid, direct);
          else this.userStudioCache[uid] = direct;
        }
        return direct;
      }
      const profileStudio = uid ? this.getUserProfile(uid)?.studio : '';
      if (profileStudio) {
        this.setOrderStudio(order, profileStudio);
        if (this.$set) this.$set(this.userStudioCache, uid, profileStudio);
        else this.userStudioCache[uid] = profileStudio;
        return profileStudio;
      }
      if (uid && Object.prototype.hasOwnProperty.call(this.userStudioCache, uid)) {
        const cached = this.userStudioCache[uid] || '';
        this.setOrderStudio(order, cached);
        return cached;
      }
      return '';
    },
    setOrderStudio(order, studio) {
      const normalized = this.normalizeStudioName(studio);
      if (this.$set) this.$set(order, '_studio', normalized);
      else order._studio = normalized;
      return normalized;
    },
    getOrderUserUid(order = {}) {
      const candidates = [
        order.userUid,
        order.userId,
        order.uid,
        order.user_uid,
        order.user_id,
        order.customerUid,
        order.customer_id,
        order.customerId,
        order.customer_uid,
        order.user?.uid,
      ];
      for (const value of candidates) {
        if (typeof value === 'string' && value.trim()) {
          return value.trim();
        }
      }
      return '';
    },
    getUserProfile(uid) {
      if (!uid) return null;
      return this.userProfileCache && Object.prototype.hasOwnProperty.call(this.userProfileCache, uid)
        ? this.userProfileCache[uid]
        : null;
    },
    normalizeStudioName(value) {
      if (value == null) return '';
      const str = String(value).trim();
      return str;
    },
    async ensureStudiosForOrders() {
      await this.ensureUserProfilesForOrders();
    },
    async ensureUserProfilesForOrders() {
      if (!this.hasAccountingAccess() || !this.db || !Array.isArray(this.orders) || !this.orders.length) return;
      const pending = [];
      for (const order of this.orders) {
        const uid = this.getOrderUserUid(order);
        if (!uid) continue;
        if (Object.prototype.hasOwnProperty.call(this.userProfileCache, uid)) {
          this.assignProfileToOrders(uid, this.userProfileCache[uid]);
          continue;
        }
        if (!this.userProfileRequests.has(uid)) {
          this.userProfileRequests.add(uid);
          pending.push(this.fetchUserProfileByUid(uid));
        }
      }
      if (pending.length) {
        try {
          await Promise.all(pending);
        } catch (err) {
          console.warn('Ordini contabilità: errore caricando i profili utente', err);
        }
      }
    },
    async fetchUserProfileByUid(uid) {
      if (!this.hasAccountingAccess() || !this.db || !uid) return;
      const accessVersion = this.accountingAccessVersion;
      try {
        const snap = await getDoc(doc(this.db, 'users', uid));
        if (!this.hasAccountingAccess(accessVersion)) return;
        const data = snap.exists() ? snap.data() : null;
        const studio = this.normalizeStudioName(data?.studio || data?.studioName || data?.parent_id);
        const profile = {
          studio,
          name: data?.name || data?.first_name || '',
          surname: data?.surname || data?.last_name || '',
        };
        if (this.$set) {
          this.$set(this.userProfileCache, uid, profile);
          this.$set(this.userStudioCache, uid, studio);
        } else {
          this.userProfileCache[uid] = profile;
          this.userStudioCache[uid] = studio;
        }
        this.assignProfileToOrders(uid, profile);
      } catch (err) {
        if (!this.hasAccountingAccess(accessVersion)) return;
        console.warn(`Ordini contabilità: impossibile recuperare il profilo per ${uid}`, err);
        if (this.$set) {
          this.$set(this.userProfileCache, uid, { studio: '', name: '', surname: '' });
          this.$set(this.userStudioCache, uid, '');
        } else {
          this.userProfileCache[uid] = { studio: '', name: '', surname: '' };
          this.userStudioCache[uid] = '';
        }
      } finally {
        if (this.hasAccountingAccess(accessVersion)) this.userProfileRequests.delete(uid);
      }
    },
    assignProfileToOrders(uid, profile = {}) {
      const normalizedStudio = this.normalizeStudioName(profile.studio);
      for (const order of this.orders) {
        if (this.getOrderUserUid(order) === uid) {
          if (normalizedStudio) this.setOrderStudio(order, normalizedStudio);
          if (this.$set) {
            this.$set(order, '_userName', profile.name || '');
            this.$set(order, '_userSurname', profile.surname || '');
          } else {
            order._userName = profile.name || '';
            order._userSurname = profile.surname || '';
          }
        }
      }
    },
    async initializeData() {
      if (!this.hasAccountingAccess()) return;
      const accessVersion = this.accountingAccessVersion;
      try {
        await this.loadProducts();
      } catch (err) {
        console.warn('Ordini contabilità: caricamento prodotti fallito', err);
      } finally {
        if (this.hasAccountingAccess(accessVersion)) await this.loadOrders();
      }
    },
    async loadProducts() {
      if (!this.hasAccountingAccess() || !this.db) return;
      const accessVersion = this.accountingAccessVersion;
      try {
        const snap = await getDocs(query(collection(this.db, 'products')));
        if (!this.hasAccountingAccess(accessVersion)) return;
        this.catalogProducts = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
        this.buildProductIndex(this.catalogProducts);
        this.productsLoaded = true;
      } catch (err) {
        if (!this.hasAccountingAccess(accessVersion)) return;
        console.warn('Ordini contabilità: impossibile caricare products', err);
        this.catalogProducts = [];
        this.buildProductIndex([]);
      }
    },
    buildProductIndex(products = []) {
      const byId = Object.create(null);
      const bySku = Object.create(null);
      const byName = Object.create(null);
      for (const prod of products) {
        if (!prod) continue;
        const idKey = this.normalizeKey(prod.id);
        if (idKey) byId[idKey] = prod;
        const skuKey = this.normalizeKey(prod.sku);
        if (skuKey) bySku[skuKey] = prod;
        const nameKey = this.normalizeKey(prod.name);
        if (nameKey) byName[nameKey] = prod;
      }
      this.productIndex = { byId, bySku, byName };
    },
    safeProducts(order) {
      const candidates = [order?.products, order?.line_items, order?.lineItems, order?.items];
      for (const candidate of candidates) {
        if (Array.isArray(candidate) && candidate.length) return candidate;
      }
      for (const candidate of candidates) {
        if (Array.isArray(candidate)) return candidate;
      }
      return [];
    },
    async loadOrders() {
      if (!this.hasAccountingAccess() || !this.db) return;
      const accessVersion = this.accountingAccessVersion;
      this.loading = true;
      this.error = '';
      this.limitedByMaxDocs = false;

      const baseRef = collection(this.db, 'orders');
      const { startDate, endDate } = this.filters;
      const hasRange = Boolean(startDate || endDate);
      const docsList = [];

      const orderDateConstraints = this.buildOrderDateConstraints({ startDate, endDate, hasRange });
      let orderDateQueryFailed = false;

      try {
        const snap = await getDocs(query(baseRef, ...orderDateConstraints));
        if (!this.hasAccountingAccess(accessVersion)) return;
        docsList.push(...snap.docs);
      } catch (primaryErr) {
        if (!this.hasAccountingAccess(accessVersion)) return;
        orderDateQueryFailed = true;
        console.warn('Ordini contabilità: query per orderDate fallita, uso fallback su importedAt', primaryErr);
        const fallbackConstraints = this.buildImportedConstraints({ startDate, endDate, hasRange, forceLimit: !hasRange });
        try {
          const fallbackSnap = await getDocs(query(baseRef, ...fallbackConstraints));
          if (!this.hasAccountingAccess(accessVersion)) return;
          docsList.push(...fallbackSnap.docs);
        } catch (fallbackErr) {
          if (!this.hasAccountingAccess(accessVersion)) return;
          console.error('Ordini contabilità: anche il fallback su importedAt è fallito', fallbackErr);
          this.error = 'Errore durante il caricamento degli ordini.';
        }
      }

      if (hasRange && !orderDateQueryFailed) {
        try {
          const supplementalSnap = await getDocs(query(baseRef, ...this.buildImportedConstraints({ startDate, endDate, hasRange })));
          if (!this.hasAccountingAccess(accessVersion)) return;
          docsList.push(...supplementalSnap.docs);
        } catch (suppErr) {
          if (!this.hasAccountingAccess(accessVersion)) return;
          console.warn('Ordini contabilità: query supplementare su importedAt fallita', suppErr);
        }
      }

      // Rate manuali: carica sempre tutti gli ordini rate_manuali indipendentemente da orderDate/importedAt
      // perché l'incasso è determinato dalle date delle singole rate.
      try {
        const manualSnap = await getDocs(query(baseRef, where('paymentMethod', '==', 'rate_manuali')));
        if (!this.hasAccountingAccess(accessVersion)) return;
        docsList.push(...manualSnap.docs);
      } catch (manualErr) {
        if (!this.hasAccountingAccess(accessVersion)) return;
        console.warn('Ordini contabilità: query su rate_manuali fallita', manualErr);
      }

      if (!hasRange) {
        this.limitedByMaxDocs = true;
      }

      try {
        const seen = new Set();
        const results = [];
        for (const docSnap of docsList) {
          if (seen.has(docSnap.id)) continue;
          seen.add(docSnap.id);
          results.push({ _id: docSnap.id, ...docSnap.data() });
        }
        this.orders = hasRange
          ? results.filter(order => this.isManualInstallmentsOrder(order) || this.isWithinDate(order))
          : results;
        await this.ensureUserProfilesForOrders();
        if (!this.hasAccountingAccess(accessVersion)) return;
        this.lastUpdatedAt = new Date();
      } catch (err) {
        if (!this.hasAccountingAccess(accessVersion)) return;
        console.error('Errore post-processing ordini:', err);
        this.error = 'Errore durante il caricamento degli ordini.';
      } finally {
        if (this.hasAccountingAccess(accessVersion)) this.loading = false;
      }
    },
    buildOrderDateConstraints({ startDate, endDate, hasRange }) {
      const constraints = [];
      if (startDate) {
        constraints.push(where('orderDate', '>=', this.composeOrderDateBoundary(startDate, 'start')));
      }
      if (endDate) {
        constraints.push(where('orderDate', '<=', this.composeOrderDateBoundary(endDate, 'end')));
      }
      constraints.push(orderBy('orderDate', 'desc'));
      if (!hasRange) {
        constraints.push(limit(this.maxDocs));
      }
      return constraints;
    },
    buildImportedConstraints({ startDate, endDate, hasRange, forceLimit = false }) {
      const constraints = [];
      if (startDate) {
        constraints.push(where('importedAt', '>=', this.composeImportedBoundary(startDate, 'start')));
      }
      if (endDate) {
        constraints.push(where('importedAt', '<=', this.composeImportedBoundary(endDate, 'end')));
      }
      constraints.push(orderBy('importedAt', 'desc'));
      if (!hasRange || forceLimit) {
        constraints.push(limit(this.maxDocs));
      }
      return constraints;
    },
    composeOrderDateBoundary(date, boundary) {
      if (!date) return '';
      return boundary === 'start' ? `${date} 00:00:00` : `${date} 23:59:59`;
    },
    composeImportedBoundary(date, boundary) {
      if (!date) return '';
      return boundary === 'start'
        ? `${date}T00:00:00.000Z`
        : `${date}T23:59:59.999Z`;
    },

    computeRange(value) {
      const today = new Date();
      const end = this.formatInputDate(today);
      const startDate = new Date(today);
      if (value === '7d') {
        startDate.setDate(today.getDate() - 6);
      } else if (value === '30d') {
        startDate.setDate(today.getDate() - 29);
      } else if (value === 'month') {
        startDate.setDate(1);
      } else if (value === 'year') {
        startDate.setMonth(0, 1);
      }
      return {
        start: this.formatInputDate(startDate),
        end,
      };
    },
    applyQuickRange(value) {
      const range = this.computeRange(value);
      this.filters.startDate = range.start;
      this.filters.endDate = range.end;
      this.activeRange = value;
    },
    syncDateRange(changed) {
      const { startDate, endDate } = this.filters;
      if (!startDate || !endDate) return;
      if (startDate > endDate) {
        if (changed === 'start') {
          this.filters.endDate = startDate;
        } else {
          this.filters.startDate = endDate;
        }
      }
    },
    detectActiveRange() {
      const { startDate, endDate } = this.filters;
      if (!startDate || !endDate) return null;
      for (const range of this.quickRanges) {
        const { start, end } = this.computeRange(range.value);
        if (start === startDate && end === endDate) {
          return range.value;
        }
      }
      return null;
    },
    isWithinDate(order) {
      const accountingDate = this.parseAccountingDate(order);
      if (!accountingDate) return false;
      const { startDate, endDate } = this.filters;
      const start = startDate ? new Date(`${startDate}T00:00:00`) : null;
      const end = endDate ? new Date(`${endDate}T23:59:59`) : null;
      if (start && accountingDate < start) return false;
      if (end && accountingDate > end) return false;
      return true;
    },
    parseAccountingDate(order = {}) {
      // Gli ordini normali sono contabilizzati una sola volta alla data ordine.
      // Le date dei pagamenti successivi (es. rate Alma) restano informative e
      // non devono spostare l'intero totale dell'ordine nei mesi successivi.
      if (!order._manualInstallmentPart) {
        return this.parseOrderDate(order);
      }

      // Le rate manuali, al contrario, sono veri movimenti separati e vengono
      // contabilizzate alla data di pagamento della singola rata.
      const installment = Array.isArray(order.manualInstallments)
        ? order.manualInstallments[0]
        : null;
      return (
        this.resolveInstallmentPaidAt(installment, order) ||
        this.toDate(order._manualInstallmentPaidAt || order.accountingDate) ||
        this.parseOrderDate(order)
      );
    },
    parseOrderDate(order) {
      const candidates = [
        order.orderDate,
        order.order_date,
        order.createdAt,
        order.creationDate,
        order.importedAt,
        order.updatedAt,
      ];
      for (const value of candidates) {
        const date = this.toDate(value);
        if (date) return date;
      }
      return null;
    },
    toDate(value) {
      if (!value) return null;
      if (typeof value.toDate === 'function') {
        try { return value.toDate(); } catch (err) { return null; }
      }
      if (typeof value === 'object' && typeof value.seconds === 'number') {
        return new Date(value.seconds * 1000);
      }
      if (typeof value === 'number') {
        const d = new Date(value);
        return Number.isNaN(d.getTime()) ? null : d;
      }
      if (typeof value === 'string') {
        const normalized = value.includes('T') ? value : value.replace(' ', 'T');
        const d = new Date(normalized);
        return Number.isNaN(d.getTime()) ? null : d;
      }
      return null;
    },
    resolveAmount(order) {
      const fields = ['totalPrice', 'total', 'amount', 'grandTotal', 'orderTotal'];
      for (const field of fields) {
        const raw = order[field];
        if (raw === undefined || raw === null || raw === '') continue;
        const num = Number(raw);
        if (Number.isFinite(num)) return num;
      }
      if (Array.isArray(order.products)) {
        const sum = order.products.reduce((acc, item) => {
          const price = Number(item.price) || 0;
          const qty = Number(item.quantity) || 1;
          return acc + price * qty;
        }, 0);
        if (sum) return sum;
      }
      return 0;
    },
    isManualInstallmentsOrder(order) {
      const method = (order.paymentMethod || order.payment_method || '').toString().toLowerCase();
      if (method === 'rate_manuali') return true;
      return Array.isArray(order.manualInstallments) && order.manualInstallments.length > 0;
    },
    getPaidInstallments(order) {
      const list = Array.isArray(order.manualInstallments) ? order.manualInstallments : [];
      return list.filter((inst) => {
        const amount = Number(inst?.amount);
        if (!this.isInstallmentPaid(inst)) return false;
        return Number.isFinite(amount) && amount > 0;
      });
    },
    parseInstallmentDate(inst, order) {
      return this.resolveInstallmentPaidAt(inst, order);
    },
    buildScaledLines(order, ratio) {
      const lines = this.safeProducts(order);
      const scaled = [];
      for (const line of lines) {
        const qty = this.resolveLineQuantity(line);
        const baseAmount = this.resolveLineAmount(line);
        const scaledAmount = baseAmount * ratio;
        const unitPrice = qty ? scaledAmount / qty : scaledAmount;
        const next = {
          ...line,
          quantity: qty || 1,
          price: unitPrice,
          total: scaledAmount,
          line_total: scaledAmount,
          amount: scaledAmount,
        };
        scaled.push(next);
      }
      return scaled;
    },
    expandManualInstallmentOrders(order) {
      const paidPlan = this.getPaidInstallments(order);
      if (!paidPlan.length) return [];

      const baseTotal = this.resolveAmount(order);
      if (!baseTotal) return [];

      const orderId = order._id || order.orderId || order.id || 'order';

      return paidPlan
        .map((inst, idx) => {
          const amount = Number(inst.amount) || 0;
          const date = this.parseInstallmentDate(inst, order);
          if (!amount || !date) return null;

          const ratio = amount / baseTotal;
          const scaledLines = this.buildScaledLines(order, ratio || 0);
          const paymentMethod = inst.method || inst.paymentMethod || order.paymentMethod || order.payment_method || 'rate_manuali';
          const instKey = inst.id || inst.installmentId || inst.uuid || `inst${idx + 1}`;
          const pseudoId = `${orderId}#${instKey}`;
          const sourceCredit = Number(order.creditApplied ?? order.paymentBreakdown?.creditApplied) || 0;
          const sourceDiscount = Number(order.discount ?? order.paymentBreakdown?.discount) || 0;

          return {
            ...order,
            _id: pseudoId,
            orderId: orderId,
            _manualInstallmentPart: true,
            _manualInstallmentPaidAt: date.toISOString(),
            accountingDate: date.toISOString(),
            paymentMethod,
            paymentStatus: 'paid',
            status: 'pagato',
            manualInstallments: [inst],
            totalPrice: amount,
            amount,
            creditApplied: this.roundCurrency(sourceCredit * ratio),
            discount: this.roundCurrency(sourceDiscount * ratio),
            products: scaledLines,
          };
        })
        .filter(Boolean);
    },
    isWithinRangeDate(date) {
      if (!(date instanceof Date) || Number.isNaN(date.getTime())) return false;
      const { startDate, endDate } = this.filters;
      const start = startDate ? new Date(`${startDate}T00:00:00`) : null;
      const end = endDate ? new Date(`${endDate}T23:59:59`) : null;
      if (start && date < start) return false;
      if (end && date > end) return false;
      return true;
    },
    normalizeInstallmentStatus(value) {
      return (value || '').toString().trim().toLowerCase();
    },
    isInstallmentPaidStatus(status) {
      const normalized = this.normalizeInstallmentStatus(status);
      if (!normalized) return false;
      if (normalized === 'pagato' || normalized === 'paid' || normalized === 'completed') return true;
      if (normalized.includes('pagat') && !normalized.includes('attesa')) return true;
      return false;
    },
    isInstallmentExpiredStatus(status) {
      const normalized = this.normalizeInstallmentStatus(status);
      if (!normalized) return false;
      if (normalized === 'scaduta' || normalized === 'expired') return true;
      if (normalized.includes('scadut')) return true;
      return false;
    },
    isInstallmentPaid(inst) {
      return !!(inst?.paidAt || inst?.paid_at) || this.isInstallmentPaidStatus(inst?.status);
    },
    isInstallmentExpired(inst, order = {}) {
      return isManualInstallmentOperationallyExpired(inst, Date.now(), order);
    },
    installmentPaidAt(inst) {
      return this.toDate(inst?.paidAt || inst?.paid_at || inst?.paidDate || inst?.paid_date);
    },
    installmentDueAt(inst) {
      return this.toDate(inst?.scheduledDate);
    },
    resolveInstallmentPaidAt(inst, order) {
      if (!this.isInstallmentPaid(inst)) return null;
      const paidAt = this.installmentPaidAt(inst);
      if (paidAt) return paidAt;
      const dueAt = this.installmentDueAt(inst);
      if (dueAt) {
        const now = new Date();
        if (dueAt <= now) return dueAt;
      }
      const orderDate = this.parseOrderDate(order || {});
      if (orderDate) return orderDate;
      return dueAt || null;
    },
    isOrderPaid(order) {
      const statusRaw = (order.status || order.orderStatus || '').toString().toLowerCase();
      const paymentStatus = (order.paymentStatus || order.payment_status || '').toString().toLowerCase();
      const method = (order.paymentMethod || order.payment_method || '').toString().toLowerCase();

      const blockedStatuses = new Set([
        'cancelled',
        'wc-cancelled',
        'refunded',
        'wc-refunded',
        'pending',
        'wc-pending',
        'on-hold',
        'wc-on-hold',
        'cancellato',
        'annullato',
        'in attesa di pagamento',
        'attesa di pagamento',
        'pagamento in attesa',
        'rimborsato',
        'rimborsato completamente',
      ]);
      if (blockedStatuses.has(statusRaw)) return false;
      if (statusRaw.includes('cancelled') || statusRaw.includes('refunded')) return false;
      if (statusRaw.includes('cancellat') || statusRaw.includes('annullat')) return false;
      if (statusRaw.includes('rimborsat') || statusRaw.includes('rimborso')) return false;
      if (statusRaw.includes('attesa') && statusRaw.includes('pagament')) return false;
      if (statusRaw.includes('pending') && !statusRaw.includes('completed')) return false;

      const paidStatuses = new Set(['pagato', 'rateizzato', 'paid', 'completed', 'complete', 'processing', 'wc-completed', 'wc-processing', 'direct_purchase', 'payment_completed', 'completed_payment']);
      if (paidStatuses.has(statusRaw)) return true;
      if (statusRaw.startsWith('wc-')) {
        if (statusRaw.includes('completed') || statusRaw.includes('processing')) return true;
      }
      if (statusRaw.includes('direct') && statusRaw.includes('purchase')) return true;
      if (statusRaw.includes('payment') && statusRaw.includes('complete')) return true;

      const paidPaymentStatuses = new Set(['paid', 'completed', 'complete', 'captured', 'succeeded', 'settled']);
      if (paymentStatus.includes('rimborsat') || paymentStatus.includes('rimborso')) return false;
      if (paymentStatus.includes('attesa') && paymentStatus.includes('pagament')) return false;
      if (paidPaymentStatuses.has(paymentStatus)) return true;
      if (paymentStatus.includes('completed') || paymentStatus.includes('captured')) return true;

      if (method === 'contanti' || method === 'cash' || method === 'custom_cash') return true;
      return false;
    },
    normalizeSource(order) {
      const explicit = (order.source || order._source || '').toString().trim().toLowerCase();
      if (explicit === 'public_checkout') {
        return (order.checkoutSource || order.landingSource || 'fuscofit').toString().trim().toLowerCase();
      }
      if (explicit) return explicit;
      const checkoutChannel = (order.checkoutChannel || '').toString().trim().toLowerCase();
      if (order.publicCheckout || checkoutChannel === 'public_checkout') {
        return (order.checkoutSource || order.landingSource || 'fuscofit').toString().trim().toLowerCase();
      }
      const id = (order._id || order.orderId || '').toString().toLowerCase();
      if (id.startsWith('legacy_')) return 'legacy';
      if (id.startsWith('manual_')) return 'manuale';
      if (id.startsWith('export_')) return 'woocommerce';
      return 'woocommerce';
    },
    normalizePaymentKey(order) {
      const methodRaw = (order.paymentMethod || order.payment_method || '').toString().trim().toLowerCase();
      const providerRaw = (order.paymentProvider || order.payment_provider || '').toString().trim().toLowerCase();
      const titleRaw = (order.payment_method_title || order.paymentMethodTitle || '').toString().trim().toLowerCase();
      const raw = methodRaw || providerRaw || titleRaw;
      if (!raw) return 'unknown';
      if (methodRaw === 'rate_manuali') return 'rate_manuali';
      if (raw.includes('paypal') || raw === 'ppcp-gateway') return 'paypal';
      if (raw.includes('alma')) return 'alma';
      if (raw.includes('stripe') || raw.includes('card') || raw.includes('credit')) return 'card';
      if (raw.includes('contanti') || raw.includes('cash')) return 'contanti';
      if (raw.includes('bonifico') || raw.includes('bank') || raw.includes('bacs')) return 'bonifico';
      if (raw.includes('satispay')) return 'satispay';
      return raw.replace(/[^a-z0-9]+/g, '_');
    },
    resolveWelfareProvider(order = {}) {
      const methodRaw = (order.paymentMethod || order.payment_method || '').toString().trim().toLowerCase();
      if (methodRaw !== 'welfare') return '';
      const provider = (order.paymentProvider || order.payment_provider || '').toString().trim();
      return provider || '';
    },
    normalizeKey(value) {
      if (value === undefined || value === null) return '';
      return String(value).trim().toLowerCase();
    },
    resolveLineNameCandidate(line = {}) {
      const candidates = [
        line.name,
        line.title,
        line.productName,
        line.product_name,
        line.product_title,
        line.item_name,
        line.itemName,
      ];
      for (const candidate of candidates) {
        if (candidate) return String(candidate);
      }
      return '';
    },
    resolveLineDisplayName(line = {}, catalogProduct = {}) {
      const fromLine = this.resolveLineNameCandidate(line);
      if (fromLine) return fromLine;
      if (catalogProduct?.name) return catalogProduct.name;
      return 'Senza nome';
    },
    normalizeProductKey(line = {}) {
      const idCandidates = [
        line.productId,
        line.product_id,
        line.id,
        line.itemId,
        line.catalogId,
      ];
      for (const candidate of idCandidates) {
        const key = this.normalizeKey(candidate);
        if (key) return `id:${key}`;
      }
      const skuCandidates = [line.sku, line.SKU, line.productSku];
      for (const candidate of skuCandidates) {
        const key = this.normalizeKey(candidate);
        if (key) return `sku:${key}`;
      }
      const nameKey = this.normalizeKey(this.resolveLineNameCandidate(line));
      if (nameKey) return `name:${nameKey}`;
      return '';
    },
    matchCatalogProduct(line = {}) {
      const { byId, bySku, byName } = this.productIndex || {};
      const idCandidates = [
        line.productId,
        line.product_id,
        line.id,
        line.itemId,
        line.catalogId,
      ];
      for (const candidate of idCandidates) {
        const key = this.normalizeKey(candidate);
        if (key && byId && byId[key]) return byId[key];
      }
      const skuCandidates = [line.sku, line.SKU, line.productSku];
      for (const candidate of skuCandidates) {
        const key = this.normalizeKey(candidate);
        if (key && bySku && bySku[key]) return bySku[key];
      }
      const nameKey = this.normalizeKey(this.resolveLineNameCandidate(line));
      if (nameKey && byName && byName[nameKey]) return byName[nameKey];
      return null;
    },


    hasPtQuantity(entity = {}) {
      if (!entity || typeof entity !== 'object') return false;
      const fields = [
        'pt_quantity',
        'ptQty',
        'ptqty',
        'ptSessions',
        'pt_sessions',
        'bonus_pt',
      ];
      return fields.some((field) => {
        const value = Number(entity[field]);
        return Number.isFinite(value) && value > 0;
      });
    },
    isPtCategoryKey(raw) {
      const direct = this.normalizeKey(raw);
      if (!direct) return false;
      if (direct === 'pt') return true;
      const presets = new Set([
        'servizi_pt',
        'servizi-pt',
        'pacchetti_pt',
        'pt_pack',
        'sedute_pt',
        'sedute-pt',
        'pt_studio',
        'studio_pt',
      ]);
      if (presets.has(direct)) return true;
      if (direct.includes('servizi') && direct.includes('pt')) return true;
      if (direct.includes('sedute') && direct.includes('pt')) return true;
      if (direct.includes('personal') && direct.includes('trainer')) return true;
      if (direct.includes('studio') && direct.includes('pt')) return true;
      return false;
    },
    normalizeCategory(raw, line = {}) {
      if (this.isPtCategoryKey(raw)) return 'pt';
      const direct = this.normalizeKey(raw);
      if (direct === 'adjustments') return 'adjustments';
      const nameKey = this.normalizeKey(this.resolveLineNameCandidate(line));
      if (this.isPtProductName(nameKey)) return 'pt';
      if (raw) return 'coaching';
      return '';
    },
    resolveProductCategory(line = {}, order = {}) {
      if (this.hasPtQuantity(line)) return 'pt';
      const candidate = line.category || line.productCategory || line.type || line.group;
      const normalizedDirect = this.normalizeCategory(candidate, line, order);
      if (normalizedDirect) return normalizedDirect;
      const catalogProduct = this.matchCatalogProduct(line);
      if (catalogProduct) {
        if (this.hasPtQuantity(catalogProduct)) return 'pt';
        const fromCatalog = this.normalizeCategory(
          catalogProduct.category || catalogProduct.product_category || catalogProduct.type,
          catalogProduct,
          order
        );
        if (fromCatalog) return fromCatalog;
        if (this.isPtProductName(this.normalizeKey(catalogProduct.name))) return 'pt';
      }
      if (this.isPtProductName(this.normalizeKey(this.resolveLineNameCandidate(line)))) return 'pt';
      return 'coaching';
    },
    isPtProductName(nameKey) {
      if (!nameKey) return false;
      if (nameKey === 'pt') return true;
      if (this.isPtCategoryKey(nameKey)) return true;
      const safe = nameKey.replace(/[^a-z0-9]+/g, ' ').replace(/\s+/g, ' ').trim();
      if (safe.includes('sedute pt') || safe.includes('pt sedute')) return true;
      if (/\bpt\b/.test(safe)) return true;
      return /\b(\d+\s*(sedute\s*)?pt|pt\s*\d+|pack\s*pt|pacchetti?\s*pt|servizi?\s*pt|sedute\s*pt)\b/.test(safe);
    },
    categoryLabel(category) {
      const direct = this.normalizeKey(category);
      if (direct === 'adjustments') return 'Differenze / Sconti / Spese';
      const norm = this.normalizeCategory(category);
      if (norm === 'pt') return 'Servizi PT';
      if (norm === 'coaching' || !norm) return 'Coaching';
      return this.formatLabel(category);
    },
    resolveLineQuantity(line = {}) {
      const qtyFields = ['quantity', 'qty', 'amount_qty', 'line_qty'];
      for (const field of qtyFields) {
        const raw = Number(line[field]);
        if (Number.isFinite(raw) && raw !== 0) return Math.abs(raw);
      }
      return 1;
    },
    resolveLineAmount(line = {}) {
      const amountFields = ['total', 'line_total', 'subtotal', 'amount', 'line_subtotal', 'row_total'];
      for (const field of amountFields) {
        const raw = Number(line[field]);
        if (Number.isFinite(raw) && raw !== 0) return raw;
      }
      const priceFields = ['price', 'unit_price', 'line_price'];
      for (const field of priceFields) {
        const raw = Number(line[field]);
        if (Number.isFinite(raw) && raw !== 0) {
          return raw * this.resolveLineQuantity(line);
        }
      }
      const fallbackPrice = Number(line.price);
      if (Number.isFinite(fallbackPrice)) {
        return fallbackPrice * this.resolveLineQuantity(line);
      }
      return 0;
    },
    resolveLineAccountingAmount(line = {}, order = {}) {
      const rawAmount = this.resolveLineAmount(line);
      if (!Number.isFinite(rawAmount) || rawAmount === 0) return 0;

      const products = this.safeProducts(order);
      if (!products.length) return this.roundCurrency(rawAmount);

      const creditApplied = Number(order.creditApplied ?? order.paymentBreakdown?.creditApplied) || 0;
      if (!Number.isFinite(creditApplied) || creditApplied <= 0) {
        return this.roundCurrency(rawAmount);
      }

      const grossLinesTotal = products.reduce((sum, item) => {
        const amount = this.resolveLineAmount(item);
        return Number.isFinite(amount) ? sum + amount : sum;
      }, 0);
      if (!Number.isFinite(grossLinesTotal) || Math.abs(grossLinesTotal) <= 0.01) {
        return this.roundCurrency(rawAmount);
      }

      const factor = Math.max(0, (grossLinesTotal - creditApplied) / grossLinesTotal);
      return this.roundCurrency(rawAmount * factor);
    },
    resolveOrderAdjustments(order = {}) {
      const products = this.safeProducts(order);
      if (!products.length) return 0;
      let distributed = 0;
      let sawAmount = false;
      for (const line of products) {
        const lineAmount = this.resolveLineAmount(line);
        if (!Number.isFinite(lineAmount) || lineAmount === 0) continue;
        distributed += lineAmount;
        sawAmount = true;
      }
      if (!sawAmount || distributed === 0) return 0;
      const orderAmount = this.resolveAmount(order);
      return this.roundCurrency(distributed - orderAmount);
    },
    resolveOrderLineTotal(order = {}) {
      const products = this.safeProducts(order);
      if (!products.length) return this.resolveAmount(order);
      let distributed = 0;
      let sawAmount = false;
      for (const line of products) {
        const lineAmount = this.resolveLineAmount(line);
        if (!Number.isFinite(lineAmount) || lineAmount === 0) continue;
        distributed += lineAmount;
        sawAmount = true;
      }
      if (!sawAmount || distributed === 0) return this.resolveAmount(order);
      return this.roundCurrency(distributed);
    },
    paymentLabel(key) {
      const map = {
        paypal: 'PayPal',
        alma: 'AlmaPay',
        card: 'Carta di credito',
        contanti: 'Contanti',
        bonifico: 'Bonifico',
        rate_manuali: 'Rate manuali',
        satispay: 'Satispay',
        unknown: 'Non specificato',
      };
      return map[key] || this.formatLabel(key);
    },
    formatCurrency(value) {
      return Number(value || 0).toLocaleString('it-IT', { style: 'currency', currency: 'EUR', minimumFractionDigits: 2, maximumFractionDigits: 2 });
    },
    roundCurrency(value) {
      const num = Number(value);
      if (!Number.isFinite(num)) return 0;
      return Math.round(num * 100) / 100;
    },
    formatNumber(value) {
      return Number(value || 0).toLocaleString('it-IT');
    },
    formatPercent(value) {
      return Number(value || 0).toLocaleString('it-IT', { style: 'percent', minimumFractionDigits: 1, maximumFractionDigits: 1 });
    },
    formatDateKey(date) {
      const pad = n => String(n).padStart(2, '0');
      return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
    },
    formatDisplayDate(key) {
      const date = new Date(`${key}T00:00:00`);
      return Number.isNaN(date.getTime())
        ? key
        : date.toLocaleDateString('it-IT', { day: '2-digit', month: 'short', weekday: 'short' });
    },
    formatSummaryDate(key) {
      const date = new Date(`${key}T00:00:00`);
      return Number.isNaN(date.getTime()) ? key : date.toLocaleDateString('it-IT');
    },
    formatDateTime(date) {
      return date.toLocaleString('it-IT');
    },
    formatLabel(value) {
      if (!value) return '—';
      return value
        .toString()
        .replace(/[_-]+/g, ' ')
        .replace(/\b\w/g, char => char.toUpperCase());
    },
    formatInputDate(date) {
      const pad = n => String(n).padStart(2, '0');
      return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
    },
    buildDailySummaryRows(orders = []) {
      const map = new Map();
      for (const order of orders) {
        const when = this.parseAccountingDate(order);
        if (!when) continue;
        const key = this.formatDateKey(when);
        if (!map.has(key)) {
          map.set(key, { date: key, totalNet: 0, welfareNet: 0 });
        }
        const entry = map.get(key);
        const lineTotal = this.resolveOrderLineTotal(order);
        const adjustment = this.resolveOrderAdjustments(order);
        const netAmount = lineTotal - adjustment;
        entry.totalNet += netAmount;
        if (this.normalizePaymentKey(order) === 'welfare') {
          entry.welfareNet += netAmount;
        }
      }
      return Array.from(map.values())
        .sort((a, b) => (a.date < b.date ? -1 : 1))
        .map((entry) => {
          const totalNet = this.roundCurrency(entry.totalNet);
          const welfareNet = this.roundCurrency(entry.welfareNet);
          return {
            date: this.formatSummaryDate(entry.date),
            totalNet,
            welfareNet,
            difference: this.roundCurrency(totalNet - welfareNet),
          };
        });
    },
    exportCsv() {
      if (!this.hasAccountingAccess()) return;
      try {
        const rows = this.buildExportRows(this.exportableOrders);
        if (!rows.length) {
          alert('Nessun ordine da esportare nel periodo selezionato.');
          return;
        }
        const headers = [
          'orderId',
          'orderNumber',
          'orderDate',
          'accountingDate',
          'importedAt',
          'status',
          'paymentStatus',
          'paymentMethod',
          'paymentMethodRaw',
          'welfareProvider',
          'source',
          'studio',
          'customerEmail',
          'customerFirstName',
          'customerLastName',
          'customerPhone',
          'userUid',
          'totalAmount',
          'currency',
          'coupon',
          'note',
          'productId',
          'productSku',
          'productName',
          'productCategory',
          'productCategoryKey',
          'productQuantity',
          'productTotal',
          'adjustments',
          'productKey',
        ];
        const separator = ';';
        const csvLines = [
          headers.join(separator),
          ...rows.map(row => headers.map(key => this.escapeCsvValue(row[key])).join(separator)),
        ];

        this.downloadCsv(csvLines.join('\n'), this.csvFilename());
      } catch (err) {
        console.error('Ordini contabilità: esportazione CSV fallita', err);
        alert('Errore durante l\'esportazione CSV.');
      }
    },
    exportDailySummaryCsv() {
      if (!this.hasAccountingAccess()) return;
      try {
        const summaryRows = this.buildDailySummaryRows(this.exportableOrders);
        if (!summaryRows.length) {
          alert('Nessun ordine da esportare nel periodo selezionato.');
          return;
        }
        const separator = ';';
        const summaryHeaders = [
          'data',
          'totale ordini - sconti (adjustment)',
          'totale ordini paymentMethod=Welfare - sconti',
          'differenza (col2-col3)',
        ];
        const summaryLines = [
          summaryHeaders.join(separator),
          ...summaryRows.map(row => summaryHeaders.map(key => this.escapeCsvValue({
            'data': row.date,
            'totale ordini - sconti (adjustment)': row.totalNet,
            'totale ordini paymentMethod=Welfare - sconti': row.welfareNet,
            'differenza (col2-col3)': row.difference,
          }[key])).join(separator)),
        ];
        this.downloadCsv(summaryLines.join('\n'), this.csvSummaryFilename());
      } catch (err) {
        console.error('Ordini contabilità: esportazione riepilogo fallita', err);
        alert('Errore durante l\'esportazione del riepilogo.');
      }
    },
    goToAiAnalysis() {
      if (!this.hasAccountingAccess()) return;
      this.$router.push('/orders-analytics');
    },
    csvFilename() {
      const start = this.filters.startDate || 'tutti';
      const end = this.filters.endDate || 'tutti';
      return `ordini_${start}_-${end}.csv`;
    },
    csvSummaryFilename() {
      const start = this.filters.startDate || 'tutti';
      const end = this.filters.endDate || 'tutti';
      return `ordini_riepilogo_giornaliero_${start}_-${end}.csv`;
    },
    downloadCsv(content, filename) {
      const blob = new Blob([content], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    },
    buildExportRows(orders = []) {
      const rows = [];
      for (const order of orders) {
        const base = this.buildOrderBase(order);
        const products = this.safeProducts(order);
        const adjustments = this.resolveOrderAdjustments(order);
        if (products.length) {
          products.forEach((line, index) => {
            const info = this.buildLineInfo(line, order);
            rows.push({
              ...base,
              productId: info.productId,
              productSku: info.sku,
              productName: info.displayName,
              productCategory: info.categoryLabel,
              productCategoryKey: info.categoryKey,
              productQuantity: info.quantity,
              productTotal: info.lineAmount,
              adjustments: index === 0 ? adjustments : '',
              productKey: info.productKey,
            });
          });
        } else {
          rows.push({
            ...base,
            productId: '',
            productSku: '',
            productName: '',
            productCategory: '',
            productCategoryKey: '',
            productQuantity: '',
            productTotal: '',
            adjustments,
            productKey: '',
          });
        }
      }
      return rows;
    },
    buildOrderBase(order = {}) {
      const orderDateObj = this.parseOrderDate(order);
      const accountingDateObj = this.parseAccountingDate(order);
      const importedDate = this.toDate(order.importedAt);
      const paymentKey = this.normalizePaymentKey(order);
      const uid = this.getOrderUserUid(order);
      const profile = this.getUserProfile(uid);
      const customerEmail =
        order.userEmail ||
        order.email ||
        order.customer_email ||
        order.billing?.email ||
        '';
      const customerFirstName =
        profile?.name ||
        order.customerName ||
        order.customer_name ||
        order.first_name ||
        order.firstName ||
        order.billing?.first_name ||
        order.user?.name ||
        order.name ||
        '';
      const customerLastName =
        profile?.surname ||
        order.customerSurname ||
        order.customer_surname ||
        order.last_name ||
        order.lastName ||
        order.billing?.last_name ||
        order.user?.surname ||
        order.surname ||
        '';
      const customerPhone = order.phone || order.customerPhone || order.customer_phone || order.billing?.phone || order.user?.phone || '';

      return {
        orderId: order._id || order.orderId || order.id || order.uuid || '',
        orderNumber: order.orderNumber || order.number || order.reference || order.code || '',
        orderDate: orderDateObj ? this.formatDateTime(orderDateObj) : '',
        accountingDate: accountingDateObj ? this.formatDateTime(accountingDateObj) : '',
        importedAt: importedDate ? this.formatDateTime(importedDate) : '',
        status: order.status || order.orderStatus || '',
        paymentStatus: order.paymentStatus || order.payment_status || '',
        paymentMethod: this.paymentLabel(paymentKey),
        paymentMethodRaw:
          order.paymentMethod ||
          order.payment_method ||
          order.paymentProvider ||
          order.payment_provider ||
          order.payment_method_title ||
          order.paymentMethodTitle ||
          '',
        welfareProvider: this.resolveWelfareProvider(order),
        source: this.normalizeSource(order),
        studio: this.getOrderStudio(order),
        customerEmail,
        customerFirstName,
        customerLastName,
        customerPhone,
        userUid: uid,
        totalAmount: this.resolveAmount(order),
        currency: order.currency || 'EUR',
        coupon: this.extractCoupon(order),
        note: order.note || order.notes || '',
      };
    },
    buildLineInfo(line = {}, order = {}) {
      const catalogProduct = this.matchCatalogProduct(line) || {};
      const displayName = this.resolveLineDisplayName(line, catalogProduct);
      const safeLine = { ...line, name: displayName };
      const categoryKey = this.resolveProductCategory(safeLine, order);
      return {
        displayName,
        productId: line.productId || line.product_id || line.id || catalogProduct.id || '',
        sku: line.sku || line.SKU || catalogProduct.sku || '',
        quantity: this.resolveLineQuantity(safeLine),
        lineAmount: this.resolveLineAmount(safeLine),
        categoryKey,
        categoryLabel: this.categoryLabel(categoryKey),
        productKey: this.normalizeProductKey(safeLine),
      };
    },
    extractCoupon(order = {}) {
      if (Array.isArray(order.coupons)) return order.coupons.join('|');
      if (Array.isArray(order.couponCodes)) return order.couponCodes.join('|');
      if (Array.isArray(order.coupon_codes)) return order.coupon_codes.join('|');
      if (Array.isArray(order.applied_coupons)) return order.applied_coupons.join('|');
      if (Array.isArray(order.coupon_lines)) {
        const codes = order.coupon_lines
          .map(line => line?.code || line?.coupon || line?.coupon_code)
          .filter(Boolean);
        if (codes.length) return codes.join('|');
      }
      return order.coupon || order.coupon_code || '';
    },
    escapeCsvValue(value) {
      if (value === null || value === undefined) return '';
      const raw = value instanceof Date ? value.toISOString() : String(value);
      const needsQuotes = /[";\n,]/.test(raw);
      const sanitized = raw.replace(/"/g, '""');
      return needsQuotes ? `"${sanitized}"` : sanitized;
    },
  },
};
</script>

<style scoped>
.dashboard-layout {
  display: flex;
}


.content-container {
  position: relative;
  flex: 1 1 auto;
  padding: 110px 32px 48px var(--staff-content-inset, 320px);
  min-height: 100vh;
  box-sizing: border-box;
  transition: padding-left 0.3s ease;
}

@media (max-width: 1280px) {
  .content-container {
    padding-left: var(--staff-content-inset, 280px);
  }
}

@media (max-width: 1024px) {
  .content-container {
    padding: 110px 24px 44px var(--staff-content-inset, 220px);
  }
}

@media (max-width: 900px) {
  .content-container {
    padding: 96px 20px 40px var(--staff-content-inset, 20px);
  }
}

.card-container {
  max-width: 1200px;
  margin: 0 auto 60px;
}

.titolo-card {
  margin: 12px 0 8px;
}

.titolo-card h1 {
  margin: 0;
  font-size: 2rem;
  color: #0f172a;
}

.subtitle {
  margin: 4px 0 0;
  color: #64748b;
}

.card-surface {
  background: #ffffff;
  border-radius: 18px;
  box-shadow: 0 16px 40px rgba(15, 23, 42, 0.08);
  padding: 20px 24px;
  margin-top: 18px;
}

.filters-card {
  margin-top: 24px;
}

.filters-row {
  display: flex;
  flex-wrap: wrap;
  gap: 20px;
  align-items: flex-end;
}

.filters-group {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.filters-group label {
  font-size: 0.78rem;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: #64748b;
}

.filters-group input {
  min-width: 180px;
  padding: 10px 14px;
  border-radius: 12px;
  border: 1px solid #e2e8f0;
  background: #f8fafc;
  color: #0f172a;
}

.filters-group input:focus {
  outline: none;
  border-color: #2563eb;
  background: #ffffff;
  box-shadow: 0 0 0 2px rgba(37, 99, 235, 0.15);
}

.quick-group {
  min-width: 260px;
}

.quick-range {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.filters-actions {
  margin-left: auto;
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  align-items: center;
}

.btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  padding: 10px 18px;
  border-radius: 12px;
  border: 1px solid transparent;
  font-weight: 600;
  font-size: 0.9rem;
  cursor: pointer;
  transition: transform 0.18s ease, box-shadow 0.18s ease, background 0.18s ease;
}

.btn:hover:not(:disabled) {
  transform: translateY(-1px);
  box-shadow: 0 6px 16px rgba(15, 23, 42, 0.12);
}

.btn:disabled {
  opacity: 0.55;
  cursor: not-allowed;
  box-shadow: none;
}

.btn.primary {
  background: linear-gradient(135deg, #2563eb, #4f46e5);
  color: #ffffff;
}

.btn.ghost {
  background: #f1f5f9;
  color: #1e293b;
  border-color: #e2e8f0;
}

.btn.ai {
  background: linear-gradient(135deg, #7c3aed 0%, #22c55e 100%);
  color: #ffffff;
  border-color: rgba(124, 58, 237, 0.2);
  box-shadow: 0 10px 24px rgba(124, 58, 237, 0.2);
}

.btn.tiny {
  padding: 8px 12px;
  font-size: 0.8rem;
}

.filters-note {
  margin-top: 16px;
  font-size: 0.8rem;
  color: #94a3b8;
}

.metrics-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
  gap: 16px;
}

.metric-box {
  background: linear-gradient(135deg, #f8fafc, #e2e8f0);
  border-radius: 16px;
  padding: 18px;
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.metric-label {
  font-size: 0.85rem;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: #64748b;
}

.metric-value {
  font-size: 1.6rem;
  font-weight: 700;
  color: #0f172a;
}

.metric-sub {
  font-size: 0.85rem;
  color: #475569;
}

.card-title {
  margin: 0 0 14px;
  font-size: 1.1rem;
  color: #0f172a;
}

.summary-table {
  width: 100%;
  border-collapse: collapse;
}

.debug-accordion {
  border: none;
}

.accordion-summary {
  cursor: pointer;
  font-weight: 600;
  padding: 12px 16px;
  border-radius: 12px;
  background: #f1f5f9;
  color: #0f172a;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.accordion-summary::-webkit-details-marker {
  display: none;
}

.debug-accordion[open] .accordion-summary {
  background: #e2e8f0;
}

.accordion-content {
  margin-top: 16px;
}

.summary-table th,
.summary-table td {
  padding: 12px 10px;
  text-align: left;
  border-bottom: 1px solid #e2e8f0;
}

.summary-table thead th {
  font-size: 0.78rem;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: #64748b;
}

.summary-table tbody tr:last-child td {
  border-bottom: none;
}

.muted {
  color: #94a3b8;
}

.note {
  margin-bottom: 12px;
}

.error-state {
  margin: 0;
  color: #dc2626;
  font-weight: 600;
}

@media (max-width: 768px) {
  .orders-accounting-page {
    width: 100%;
    min-width: 0;
  }

  .orders-accounting-page .content-container,
  .orders-accounting-page .content-container.sidebar-hidden {
    width: 100%;
    max-width: 100%;
    min-width: 0;
    padding: 80px 12px 40px;
  }

  .orders-accounting-page .card-container {
    min-width: 0;
    padding: 0;
  }

  .orders-accounting-page .filters-group,
  .orders-accounting-page .quick-group {
    width: 100%;
    min-width: 0;
  }

  .orders-accounting-page .filters-group input,
  .orders-accounting-page .filters-group select {
    box-sizing: border-box;
    width: 100%;
    min-width: 0;
    max-width: 100%;
  }

  .orders-accounting-page .metrics-grid {
    grid-template-columns: minmax(0, 1fr);
  }

  .orders-accounting-page .metric-value {
    overflow-wrap: anywhere;
  }

  .accounting-table-section {
    padding: 18px 12px;
  }

  .accounting-table-section .card-title {
    text-align: left;
    line-height: 1.35;
  }

  .accounting-table-section .note {
    color: #64748b;
    line-height: 1.5;
    overflow-wrap: anywhere;
  }

  .accounting-summary-table,
  .accounting-summary-table tbody {
    display: block;
    width: 100%;
    min-width: 0;
    margin: 0;
    padding: 0;
    background: transparent;
  }

  .accounting-summary-table {
    position: relative;
    color: #1f2937;
  }

  .accounting-summary-table thead {
    position: absolute;
    width: 1px;
    height: 1px;
    margin: -1px;
    padding: 0;
    overflow: hidden;
    clip-path: inset(50%);
    white-space: nowrap;
  }

  .accounting-summary-table tbody tr {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 14px 12px;
    margin-bottom: 12px;
    padding: 14px;
    border: 1px solid #dfe3e8;
    border-radius: 14px;
    background: #f3f4f6;
  }

  .accounting-summary-table tbody tr:last-child {
    margin-bottom: 0;
  }

  .accounting-summary-table td {
    display: flex;
    flex-direction: column;
    gap: 4px;
    order: 2;
    min-width: 0;
    padding: 0;
    border: 0;
    background: transparent;
    color: #1f2937;
    font-size: 15px;
    line-height: 1.4;
    font-variant-numeric: tabular-nums;
    white-space: normal;
    overflow-wrap: anywhere;
  }

  .accounting-summary-table td::before {
    content: attr(data-label);
    color: #64748b;
    font-size: 12px;
    font-weight: 500;
    line-height: 1.4;
  }

  .accounting-summary-table td.accounting-cell-title {
    grid-column: 1 / -1;
    order: 0;
    font-size: 17px;
    font-weight: 700;
    color: #0f172a;
  }

  .accounting-summary-table .accounting-cell-title::before {
    display: none;
  }

  .accounting-summary-table tbody tr td.accounting-cell-featured {
    grid-column: 1 / -1;
    order: 1;
    padding-bottom: 12px;
    border-bottom: 1px solid #d1d5db;
    font-size: 22px;
    font-weight: 700;
    color: #0f172a;
  }

  .accounting-summary-table .accounting-cell-wide {
    grid-column: 1 / -1;
  }

  .accounting-summary-table td.accounting-cell-total-count {
    order: 3;
  }

  .filters-actions {
    width: 100%;
    margin-left: 0;
  }

  .filters-actions .btn {
    width: 100%;
  }
}
</style>
